import { useMutation } from "@apollo/client";
import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import PersonAdd from "@mui/icons-material/PersonAdd";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import RemoveFilled from "@mui/icons-material/Remove";
import RestartAlt from "@mui/icons-material/RestartAlt";
import { Box, Alert, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  type GridActionsCellItemProps,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { IssueCount } from "components/IssueCount";
import {
  useDueDateWarningsByUserId,
  useReviewerIssues,
  useReviewersIssues,
} from "contexts/SubmissionFormContext";
import {
  ReviewMode,
  ReviewerStatus,
  Permission,
  SubmissionEncampStatus,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { SubmissionFormData } from "hooks/useSubmissionForm";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { RESET_SUBMISSION_REVIEWER_PROGRESS_MUTATION } from "queries/submissions";
import { useCallback, useMemo, useState } from "react";
import { UseFormSetValue } from "react-hook-form";

import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { DueDateColumnHeader, DueDateWithMessage } from "./DueDateWithMessage";
import { DUE_DATE_COLUMN_SIZING } from "./helpers/constants";
import { renderIssueMessage } from "./helpers/renderIssueMessage";
import { PriorityOrderSwitch } from "./PriorityOrderSwitch";
import { Reviewer } from "./ReviewersEditList";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

type ReviewersDatagridProps = {
  reviewers: Reviewer[];
  setReviewers?: (reviewers: Reviewer[]) => void;
  reviewMode: ReviewMode;
  setValue?: UseFormSetValue<SubmissionFormData>;
  handleAddReviewer: () => void;
  submissionDueDate?: DateTime;
  disabled?: boolean;
  isStaff?: boolean;
  submissionId?: string;
  submissionEncampStatus?: SubmissionEncampStatus | null;
  /**
   * `SubmissionReviewer.id` of the row whose turn it is AND who is the viewing
   * user — that row's pending status renders as "Your turn". The caller owns
   * the turn logic (`isActiveReviewer`); the grid just displays it.
   */
  activeReviewerId?: string;
};

function setReviewerOrder(reviewers: Reviewer[]) {
  const hasStaff = reviewers.some((r) => r.isStaff);
  reviewers.forEach((r, index) => {
    if (r.isStaff) {
      return;
    }

    r.order = hasStaff ? index : index + 1;
  });
}

export function ReviewersDatagrid({
  reviewers,
  setReviewers,
  reviewMode,
  setValue,
  handleAddReviewer,
  disabled = false,
  isStaff = false,
  submissionId,
  submissionEncampStatus,
  activeReviewerId,
}: ReviewersDatagridProps) {
  const isSubmissionRejected = submissionEncampStatus === SubmissionEncampStatus.Rejected;
  const reviewerIssues = useReviewerIssues();
  const reviewersIssues = useReviewersIssues();
  const dueDateWarningsByUserId = useDueDateWarningsByUserId();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();
  const alerts = useAlerts();

  const [resetReviewerProgress, { loading: resetting }] = useMutation(
    RESET_SUBMISSION_REVIEWER_PROGRESS_MUTATION,
    {
      refetchQueries: ["GetSubmission"],
      awaitRefetchQueries: true,
    }
  );

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  // Filter individual reviewer issues by checking if reviewer IDs appear in schemaStack
  const individualReviewerIssues = useMemo(
    () =>
      reviewerIssues.filter((i) =>
        reviewers.some((r) => i.schemaStack?.some((schemaStack) => schemaStack.name === r.id))
      ),
    [reviewerIssues, reviewers]
  );

  // Due date issues render under the date itself, so the banner would repeat them.
  const bannerIssues = useMemo(
    () => [...reviewersIssues, ...individualReviewerIssues.filter((i) => i.key !== "dueDate")],
    [reviewersIssues, individualReviewerIssues]
  );

  const [confirmAction, setConfirmAction] = useState<{
    type: "reset" | "complete";
    reviewerId: string;
    reviewerName: string;
  } | null>(null);

  const handleConfirmAction = useCallback(
    (type: "reset" | "complete", reviewerId: string, reviewerName: string) => {
      setConfirmAction({ type, reviewerId, reviewerName });
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    if (!confirmAction || !setReviewers) return;

    // Reset must hit API; save omits null reviewerStatus.
    if (confirmAction.type === "reset" && submissionId) {
      try {
        await resetReviewerProgress({
          variables: { submissionId, reviewerId: confirmAction.reviewerId },
        });
      } catch (caught) {
        if (caught instanceof Error) {
          alerts.error(caught.message, caught);
        } else {
          alerts.error("Failed to reset reviewer progress.");
        }
        setConfirmAction(null);
        return;
      }
    }

    const updatedReviewers = reviewers.map((r) => {
      if (r.submissionReviewerId !== confirmAction.reviewerId) return r;
      if (confirmAction.type === "reset") {
        return { ...r, reviewerStatus: null };
      }
      return { ...r, reviewerStatus: ReviewerStatus.Approved };
    });
    setReviewers(updatedReviewers);
    setConfirmAction(null);
  }, [alerts, confirmAction, resetReviewerProgress, reviewers, setReviewers, submissionId]);

  const augmentedReviewersRows = useMemo(
    () =>
      reviewers.map((r) => ({
        ...r,
        __reorder__: r.name,
      })),
    [reviewers]
  );
  const columns = useMemo(() => {
    const cols: GridColDef<Reviewer>[] = [];

    const issuesColumn: GridColDef<Reviewer> = {
      field: "issues",
      display: "flex",
      flex: 0.6,
      headerName: "Issues",
      renderCell: ({ row }) => {
        const issues = reviewerIssues.filter((i) => {
          return i.schemaStack?.some((s) => s.name === row.id);
        });
        return <IssueCount issueCount={issues.length} />;
      },
    };

    if (isStaff && !disabled) {
      cols.push(
        {
          field: "approval",
          display: "flex",
          width: 30,
          minWidth: 30,
          headerName: "",
          sortable: false,
          renderCell: ({ row }) => {
            if (row.reviewerStatus === ReviewerStatus.Approved) {
              return <CheckCircle color="success" fontSize="small" />;
            }
            if (row.reviewerStatus === ReviewerStatus.Rejected) {
              return <Cancel color="error" fontSize="small" />;
            }
            if (row.submissionReviewerId) {
              return (
                <Tooltip title="Mark as approved">
                  <Box
                    onClick={(event) => {
                      event.stopPropagation();
                      handleConfirmAction("complete", row.submissionReviewerId!, row.name);
                    }}
                    sx={{ cursor: "pointer", display: "flex", "&:hover": { opacity: 0.7 } }}
                  >
                    <RadioButtonUnchecked color="disabled" fontSize="small" />
                  </Box>
                </Tooltip>
              );
            }
            return <RadioButtonUnchecked color="disabled" fontSize="small" />;
          },
        },
        {
          field: "reviewerStatus",
          display: "flex",
          flex: 0.5,
          minWidth: 72,
          headerName: "Status",
          sortable: false,
          renderCell: ({ row }) => {
            if (row.reviewerStatus === ReviewerStatus.Approved) {
              return (
                <Typography variant="body2" fontWeight={700}>
                  Approved
                </Typography>
              );
            }
            if (row.reviewerStatus === ReviewerStatus.Rejected) {
              return (
                <Typography variant="body2" fontWeight={700}>
                  Rejected
                </Typography>
              );
            }
            if (row.submissionReviewerId && row.submissionReviewerId === activeReviewerId) {
              return (
                <Typography variant="body2" color="primary" fontWeight={700}>
                  Your turn
                </Typography>
              );
            }
            return <Typography variant="body2">Pending</Typography>;
          },
        }
      );
    }

    if (reviewMode === ReviewMode.Sequential) {
      cols.push({
        field: "order",
        flex: 0.3,
        minWidth: 64,
        headerName: "Order",
        align: "center",
        headerAlign: "center",
        sortable: false,
        valueGetter: (_, row) => (row.order !== 0 ? row.order : ""),
      });
    }

    cols.push(
      {
        field: "name",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Name",
        // Always show the reviewer's name (including the viewer's own row) —
        // like a GitHub reviewers list — rather than "You".
        renderCell: ({ row }) => row.name,
      },
      {
        field: "email",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Email",
      },
      {
        field: "dueDate",
        display: "flex",
        ...DUE_DATE_COLUMN_SIZING,
        sortable: false,
        renderHeader: () => <DueDateColumnHeader label="Due date" />,
        renderCell: ({ row }) => (
          <DueDateWithMessage
            dueDate={row.dueDate}
            errorMessage={
              reviewerIssues.find(
                (i) => i.key === "dueDate" && i.schemaStack?.some((s) => s.name === row.id)
              )?.message
            }
            // A persisted date that drifted into the past is grandfathered by
            // validation, so it only reaches the grid as a warning.
            warningMessage={dueDateWarningsByUserId[row.id]}
          />
        ),
      }
    );

    if (reviewerIssues.length > 0) {
      cols.push(issuesColumn);
    }

    if (!disabled) {
      cols.push({
        field: "actions",
        type: "actions",
        width: 56,
        align: "right",
        headerName: "",
        sortable: false,
        getActions: ({ row }) => {
          const hasProgress =
            row.reviewerStatus === ReviewerStatus.Approved ||
            row.reviewerStatus === ReviewerStatus.Rejected;

          const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

          // Removing or resetting the rejecting reviewer would strand the submission's
          // REJECTED encampStatus with no in-app recovery path.
          const isRejectingReviewerOnRejectedSubmission =
            isSubmissionRejected && row.reviewerStatus === ReviewerStatus.Rejected;
          const rejectingReviewerDisabledReason = isRejectingReviewerOnRejectedSubmission
            ? "Use the rejection routing dialog to resolve the rejection instead of modifying this reviewer."
            : undefined;

          if (isStaff && submissionId && row.submissionReviewerId && hasProgress) {
            actions.push(
              <GridActionsCellItem
                key="reset"
                label="Reset Progress"
                disabled={isRejectingReviewerOnRejectedSubmission}
                icon={
                  <Tooltip title={rejectingReviewerDisabledReason ?? "Reset progress"}>
                    <RestartAlt color="action" />
                  </Tooltip>
                }
                onClick={() => {
                  handleConfirmAction("reset", row.submissionReviewerId!, row.name);
                }}
              />
            );
          }

          if (!disabled) {
            actions.push(
              <GridActionsCellItem
                key="delete"
                label="Delete"
                disabled={isRejectingReviewerOnRejectedSubmission}
                icon={
                  <Tooltip title={rejectingReviewerDisabledReason ?? "Delete"}>
                    <RemoveFilled />
                  </Tooltip>
                }
                onClick={() => {
                  setReviewers?.(reviewers.filter((r) => r.id !== row.id));
                  setReviewerOrder(reviewers.filter((r) => r.id !== row.id));
                }}
              />
            );
          }

          return actions;
        },
      });
    }

    return cols;
  }, [
    reviewMode,
    reviewers,
    setReviewers,
    reviewerIssues,
    dueDateWarningsByUserId,
    disabled,
    isStaff,
    submissionId,
    isSubmissionRejected,
    handleConfirmAction,
    activeReviewerId,
  ]);

  const pinnedRows = useMemo(
    () => ({
      top: augmentedReviewersRows.filter((r) => r.isStaff), // Pin staff reviewers at the top
    }),
    [augmentedReviewersRows]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <PriorityOrderSwitch
        value={reviewMode}
        onChange={(newReviewMode: ReviewMode) =>
          setValue?.("reviewMode", newReviewMode, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        disabled={disabled}
      />
      {bannerIssues.length > 0 && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
          {bannerIssues.map((issue, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(issue.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      {disabled ? (
        <SubmissionFormDatagrid
          rows={augmentedReviewersRows}
          columns={columns}
          rowReordering={false}
          pinnedRows={pinnedRows}
        />
      ) : (
        <DataGridWithAddButton
          rows={augmentedReviewersRows}
          columns={columns}
          onAdd={handleAddReviewer}
          createEmptyRow={() => ({
            id: "empty",
            name: "",
            email: "",
            dueDate: undefined,
            streetAddress1: undefined,
            streetAddress2: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
            country: undefined,
            isSigner: false,
            isStaff: false,
            order: 0,
          })}
          rowReordering={reviewMode === ReviewMode.Sequential}
          pinnedRows={pinnedRows}
          onRowOrderChange={(params) => {
            const { oldIndex, targetIndex } = params;
            const hasStaff = reviewers.some((r) => r.isStaff);
            const o = hasStaff ? oldIndex + 1 : oldIndex;
            const n = hasStaff ? targetIndex + 1 : targetIndex;

            if (setReviewers) {
              const newList = [...reviewers];
              const [movedItem] = newList.splice(o, 1);
              newList.splice(n, 0, movedItem);
              setReviewerOrder(newList);
              setReviewers(newList);

              // Transform reviewers from UI format to form format and trigger validation
              const formReviewers = newList.map((r) => ({
                userId: r.id,
                reviewOrder: r.order,
                isRequired: true,
                isSigner: r.isSigner ?? false,
                isStaff: r.isStaff,
                dueDate: r.dueDate,
              }));

              setValue?.("reviewers", formReviewers, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }}
          addButtonConfig={{
            buttonText: "Add Reviewer",
            icon: <PersonAdd />,
          }}
          gridProps={{
            onRowClick: handleAddReviewer,
          }}
        />
      )}
      <ConfirmDialog
        open={!!confirmAction}
        loading={confirmAction?.type === "reset" ? resetting : undefined}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        header={
          confirmAction?.type === "reset" ? "Reset Reviewer Progress" : "Mark Reviewer Complete"
        }
        info={
          confirmAction?.type === "reset"
            ? `This will clear all progress for ${confirmAction?.reviewerName ?? "this reviewer"}, including approval status and document views.`
            : `This will mark ${confirmAction?.reviewerName ?? "this reviewer"} as approved and all their documents as viewed. This is a staff override.`
        }
        confirmText={confirmAction?.type === "reset" ? "Yes, Reset" : "Yes, Complete"}
      />
    </Box>
  );
}
